import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import {
  createChildCompanyAdmin,
  createCompanyAdmin,
  createCompanyAdminByAdmin,
  createStaff,
  deleteStaff,
  getIsSupportingCompanyAdmin,
  getStaffInfo,
  getStaffList,
  getSupportedStaffList,
  updateStaffDetailsById,
  uploadStaffImage,
  getStaffCreationJobStatus,
} from "../api-services/brokers";
import { QUERY_CACHE_TIME, STAFF_ROLE, USER_ROLE } from "../commons/enum";
import { useIsLoggedIn, useMyInfo } from "./user.hooks";

export const useMyStaffInfo = () => {
  const isLoggedIn = useIsLoggedIn();
  return useQuery("myStaffInfo", () => getStaffInfo(), {
    enabled: isLoggedIn,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteStaff, {
    onSuccess: () => {
      return queryClient.invalidateQueries("staffList");
    },
  });
};

export const useStaffList = (
  { search, companyIds, sortBy, sortDirection, staffRoles, enabled = true } = {},
  limit = 10,
  page = 1
) => {
  const isLoggedIn = useIsLoggedIn();
  const { data: userInfo } = useMyInfo();
  const { data: staffInfo } = useMyStaffInfo();
  const isCompanyAdmin = staffInfo?.staffRoles.some(
    (role) => role.role === STAFF_ROLE.COMPANY_ADMIN
  );
  const isEnabled = useMemo(() => {
    return enabled && isLoggedIn && (userInfo?.userRole === USER_ROLE.ADMIN || isCompanyAdmin);
  }, [enabled, isLoggedIn, userInfo, isCompanyAdmin]);
  return useQuery(
    ["staffList", search, companyIds, sortBy, sortDirection, staffRoles, limit, page],
    () => getStaffList({ search, companyIds, sortBy, sortDirection, staffRoles }, limit, page),
    {
      enabled: isEnabled,
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
    }
  );
};

export const useUpdateStaffImage = () => {
  return useMutation(uploadStaffImage);
};

export const useUpdateStaffById = ({ selfUpdate = false }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(
    (data) => {
      return updateStaffDetailsById(data.id, data.params);
    },
    {
      onSuccess: () => {
        if (selfUpdate) {
          queryClient.invalidateQueries("myStaffInfo");
          enqueueSnackbar("Your information has been updated", {
            variant: "success",
          });
        }
      },
    }
  );
};

export const useCreateCompanyAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation(createCompanyAdmin, {
    onSuccess: () => {
      return queryClient.invalidateQueries("companyList");
    },
  });
};

export const useCreateChildCompanyAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation(createChildCompanyAdmin, {
    onSuccess: () => {
      return queryClient.invalidateQueries("companyList");
    },
  });
};

export const useCreateCompanyAdminByAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation(createCompanyAdminByAdmin, {
    onSuccess: () => {
      return queryClient.invalidateQueries("companyList");
    },
  });
};

export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  return useMutation(createStaff, {
    onSuccess: () => {
      return queryClient.invalidateQueries("staffList");
    },
  });
};

export const useInfiniteStaffList = (
  { search, companyIds, includeStatistics, sortBy, sortDirection, staffRoles } = {},
  limit = 10
) => {
  const isLoggedIn = useIsLoggedIn();

  return useInfiniteQuery(
    [
      "infiniteStaffList",
      search,
      companyIds,
      includeStatistics,
      sortBy,
      sortDirection,
      staffRoles,
      limit,
    ],
    ({ pageParam = 1 }) =>
      getStaffList(
        {
          search,
          companyIds,
          includeStatistics,
          sortBy,
          sortDirection,
          staffRoles,
        },
        limit,
        pageParam
      ),
    {
      enabled: isLoggedIn,
      getNextPageParam: (lastPage, allPages) => {
        /**
         * * Check if there are more pages available
         */
        const currentPageNumber = allPages.length;
        const totalPages = Math.ceil(lastPage.total / limit);
        return currentPageNumber < totalPages ? currentPageNumber + 1 : undefined;
      },
    }
  );
};

export const useIsSupportRole = () => {
  const { data: userInfo } = useMyInfo();
  const { data: staffInfo } = useMyStaffInfo();
  return useMemo(() => {
    if (userInfo?.userRole !== USER_ROLE.STAFF) return false;
    return staffInfo?.staffRoles?.some((role) => role.role === STAFF_ROLE.SUPPORT);
  }, [userInfo?.userRole, staffInfo?.staffRoles]);
};

export const useIsSupportingCompanyAdmin = () => {
  const isSupportRole = useIsSupportRole();
  return useQuery("isSupportingCompanyAdmin", () => getIsSupportingCompanyAdmin(), {
    enabled: !!isSupportRole,
    refetchOnWindowFocus: false,
  });
};

export const useSupportedStaffList = ({ search, withStats } = {}, limit = 10, page = 1) => {
  const isLoggedIn = useIsLoggedIn();
  const isSupportRole = useIsSupportRole();
  return useQuery(
    ["supportedStaffList", search, withStats, limit, page],
    () => getSupportedStaffList({ search, withStats }, limit, page),
    {
      enabled: isLoggedIn && !!isSupportRole,
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
      select: (data) => ({
        ...data,
        data: data.data?.map((item) => ({
          ...item.leadStaff,
          staffSupportId: item.id,
        })),
      }),
    }
  );
};

export const useInfiniteSupportedStaffList = ({ search } = {}, limit = 10) => {
  const isLoggedIn = useIsLoggedIn();
  const isSupportRole = useIsSupportRole();

  return useInfiniteQuery(
    ["infiniteSupportedStaffList", search, limit],
    ({ pageParam = 1 }) => getSupportedStaffList({ search }, limit, pageParam),
    {
      enabled: isLoggedIn && !!isSupportRole,
      getNextPageParam: (lastPage, allPages) => {
        const currentPageNumber = allPages.length;
        const totalPages = Math.ceil(lastPage.total / limit);
        return currentPageNumber < totalPages ? currentPageNumber + 1 : undefined;
      },
      select: (data) => ({
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: page.data?.map((item) => ({
            ...item.leadStaff,
            staffSupportId: item.id,
          })),
        })),
      }),
    }
  );
};

export const useSupportedStaffListByStaffId = ({ staffId, search } = {}, limit = 10, page = 1) => {
  return useQuery(
    ["supportedStaffListByStaffId", staffId, search, limit, page],
    () => getSupportedStaffList({ staffId, search, withStats: false }, limit, page),
    {
      enabled: !!staffId,
      refetchOnWindowFocus: false,
      select: (data) => ({
        data:
          data.data?.map((item) => ({
            ...item.leadStaff,
            staffSupportId: item.id,
          })) || [],
        total: data.total || 0,
      }),
    }
  );
};

export const useStaffCreationJobStatus = () => {
  const isLoggedIn = useIsLoggedIn();
  const query = useQuery("staffCreationJobStatus", getStaffCreationJobStatus, {
    enabled: isLoggedIn,
    refetchOnWindowFocus: false,
    refetchInterval: (data) => {
      // Poll every 30s while there are pending jobs
      return data?.hasPending ? 30_000 : false;
    },
  });
  return query;
};
